import { Button } from 'primereact/button';
import { type MouseEvent, type ReactNode } from 'react';

import { cn } from '@shared/lib/cn';

export type BaseIconButtonVariant = 'outlined' | 'ghost';
export type BaseIconButtonSize = 'sm' | 'md' | 'lg';

export interface BaseIconButtonProps {
  /** PrimeIcons class, e.g. `pi pi-bars`. */
  icon: string;
  /** Required: an icon alone has no accessible name. */
  'aria-label': string;
  variant?: BaseIconButtonVariant;
  size?: BaseIconButtonSize;
  /** Circle instead of the rounded square. */
  round?: boolean;
  disabled?: boolean;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  title?: string;
  /** Overlay content drawn on top of the icon, e.g. an unread badge. */
  children?: ReactNode;
}

const SIZE_CLASS: Record<BaseIconButtonSize, { box: string; icon: string }> = {
  sm: { box: 'size-6.5', icon: 'text-xs' },
  md: { box: 'size-7', icon: 'text-[13px]' },
  lg: { box: 'size-8.5', icon: 'text-[15px]' },
};

/** Square (or round) icon-only button: rail toggle, sign out, inbox bell. */
export function BaseIconButton({
  icon,
  'aria-label': ariaLabel,
  variant = 'outlined',
  size = 'md',
  round = false,
  disabled = false,
  onClick,
  className,
  title,
  children,
}: BaseIconButtonProps) {
  return (
    <Button
      type="button"
      disabled={disabled}
      onClick={onClick}
      title={title}
      aria-label={ariaLabel}
      className={cn(
        'cd-icon-button',
        variant === 'ghost' && 'p-button-text',
        round && 'rounded-full',
        SIZE_CLASS[size].box,
        className,
      )}
    >
      <i className={cn(icon, SIZE_CLASS[size].icon)} aria-hidden />
      {children}
    </Button>
  );
}
